/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
    Snackbar,
    Button,
    Divider,
    Tooltip,
    Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { useSelector } from 'react-redux';
import api from '../../utils/axiosConfig';
import { usePWAStatus } from '../../hooks/usePWAStatus';
import { notifications, runtime } from '../../platform';

const NotificationPreferencesEditor = ({ customerKey, caregiverId, caregiverName, smartConnectUnit = false, useApp = 1 }) => {
    const loggedInUser = useSelector((state) => state.auth.user);
    const { isStandalone } = usePWAStatus();
    const isAppShell = runtime.isNative() || isStandalone;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saveToastOpen, setSaveToastOpen] = useState(false);
    const [saveToastMessage, setSaveToastMessage] = useState('Notification preferences updated successfully!');
    const [pushStatusUpdating, setPushStatusUpdating] = useState(false);
    const [preferences, setPreferences] = useState([]);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [hasActivePushSubscription, setHasActivePushSubscription] = useState(false);
    const [activePushSubscriptions, setActivePushSubscriptions] = useState([]);
    const [pushPermission, setPushPermission] = useState('default');
    const deliveryMethodTooltips = {
        email: 'Send notifications to the caregiver\'s registered email address. They will receive detailed email notifications that they can review at their convenience.',
        push: 'Send instant push notifications to the caregiver\'s mobile device. These appear as pop-up alerts even when the app is not open.',
        sms: 'Send text message alerts to the caregiver\'s mobile phone. They will receive instant SMS notifications for critical emergency alerts.'
    };
    const isEditingOwnPreferences = loggedInUser && String(loggedInUser.user) === String(caregiverId);
    const canEditEnablePush = isEditingOwnPreferences && isAppShell;
    const showDeviceScopedStatus = isEditingOwnPreferences && isAppShell;
    const enablePushTooltip = !isEditingOwnPreferences
        ? `${deliveryMethodTooltips.push} Only this caregiver can change this setting from their own app.`
        : !isAppShell
            ? `${deliveryMethodTooltips.push} Open Connect App 365 from your home screen to change this setting.`
            : deliveryMethodTooltips.push;
    const hasPushTemplatesEnabled = preferences.some((pref) => !!pref.PushEnabled);

    const getPushPreferenceTooltip = (isTemplatePushEnabled) => {
        if (!hasActivePushSubscription && isTemplatePushEnabled) {
            return 'This notification is set to Push, but this caregiver has not allowed Push Notifications on any devices.';
        }
        if (!hasActivePushSubscription) {
            return 'Turn on "Allow Push Notifications" above to edit push settings.';
        }
        return deliveryMethodTooltips.push;
    };

    useEffect(() => {
        void fetchPreferences();
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') void fetchPreferences();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerKey, caregiverId, smartConnectUnit, useApp]);

    const fetchPreferences = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/api/caregivers/${customerKey}/${caregiverId}/notification-preferences`);
            setPreferences(response.data.preferences || []);
            const activeSubs = response.data.activePushSubscriptions || [];
            let hasActiveOnCurrentDevice = response.data.hasActivePushSubscription || activeSubs.length > 0;

            // For the logged-in caregiver using installed app mode, status should reflect this exact device.
            if (showDeviceScopedStatus) {
                const currentStatus = await notifications.getStatus();
                const currentSubscription = currentStatus.subscription;
                const currentEndpoint = currentSubscription?.endpoint || currentSubscription?.toJSON?.()?.endpoint;
                setPushPermission(currentStatus.permission);

                if (!currentStatus.enabled || !currentEndpoint) {
                    hasActiveOnCurrentDevice = false;
                } else {
                    hasActiveOnCurrentDevice = activeSubs.some((sub) => {
                        if (sub.Endpoint) {
                            return sub.Endpoint === currentEndpoint;
                        }

                        // Fallback for older responses that might include serialized subscription JSON.
                        if (sub.PushSubscription) {
                            try {
                                const parsed = typeof sub.PushSubscription === 'string'
                                    ? JSON.parse(sub.PushSubscription)
                                    : sub.PushSubscription;
                                return parsed?.endpoint === currentEndpoint;
                            } catch {
                                return false;
                            }
                        }

                        return false;
                    });
                }
            }

            setHasActivePushSubscription(hasActiveOnCurrentDevice);
            setActivePushSubscriptions(activeSubs);
            // Filter templates based on unit type and one-time notifications
            const templates = response.data.availableTemplates || [];
            const filteredTemplates = templates.filter(tmpl => {
                // Hide welcome_new_caregiver - it's a one-time notification
                if (tmpl.TemplateName === 'welcome_new_caregiver') {
                    return false;
                }
                // Show smart_connect_alert_received for everyone, but hide other smart_connect templates for non-SmartConnect units
                if (tmpl.TemplateName.includes('smart_connect') && tmpl.TemplateName !== 'smart_connect_alert_received' && !smartConnectUnit) {
                    return false;
                }
                // For non-app users, only show templates flagged as ShowForNonAppUsers
                if (useApp === 0 && !tmpl.ShowForNonAppUsers) {
                    return false;
                }
                return true;
            });
            
            setAvailableTemplates(filteredTemplates);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching notification preferences:', err);
            setError('Failed to load notification preferences');
            setLoading(false);
        }
    };

    const handlePushSubscriptionToggle = async (enabled) => {

        try {
            setPushStatusUpdating(true);
            setError('');
            setSaveToastOpen(false);

            if (enabled) {
                await notifications.enable();
            } else {
                try {
                    await notifications.disable();
                } catch (cleanupError) {
                    console.warn('Push unsubscribe cleanup failed:', cleanupError);
                }
            }

            await fetchPreferences();
            setSaveToastMessage('Push notification setting updated successfully!');
            setSaveToastOpen(true);
        } catch (err) {
            console.error('Error updating push subscription status:', err);
            setError(err.message || 'Failed to update push notification status');
        } finally {
            setPushStatusUpdating(false);
        }
    };

    const handleToggle = (templateName, category, field) => {
        setPreferences(prevPrefs => {
            const existingPref = prevPrefs.find(
                p => p.TemplateName === templateName && p.Category === category
            );

            if (existingPref) {
                return prevPrefs.map(p =>
                    p.TemplateName === templateName && p.Category === category
                        ? { ...p, [field]: p[field] ? 0 : 1 }
                        : p
                );
            } else {
                // Create new preference
                return [
                    ...prevPrefs,
                    {
                        Category: category,
                        TemplateName: templateName,
                        EmailEnabled: field === 'EmailEnabled' ? 1 : 0,
                        PushEnabled: field === 'PushEnabled' ? 1 : 0,
                        InAppEnabled: 1, // Always enabled by default
                        SMSEnabled: field === 'SMSEnabled' ? 1 : 0,
                        QuietHoursStart: null,
                        QuietHoursEnd: null
                    }
                ];
            }
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSaveToastOpen(false);

            await api.put(`/api/caregivers/${customerKey}/${caregiverId}/notification-preferences`, {
                preferences
            });

            setSaveToastMessage('Notification preferences updated successfully!');
            setSaveToastOpen(true);
        } catch (err) {
            console.error('Error saving notification preferences:', err);
            setError(err.response?.data?.error || 'Failed to save notification preferences');
        } finally {
            setSaving(false);
        }
    };

    const getPreferenceValue = (templateName, category, field) => {
        const pref = preferences.find(
            p => p.TemplateName === templateName && p.Category === category
        );
        return pref ? !!pref[field] : (field === 'InAppEnabled' ? true : false);
    };

    // Group templates by category
    const groupedTemplates = availableTemplates.reduce((acc, template) => {
        if (!acc[template.Category]) {
            acc[template.Category] = [];
        }
        acc[template.Category].push(template);
        return acc;
    }, {});

    const getCategoryLabel = (category) => {
        const labels = {
            'system': 'System Notifications',
            'caregiver': 'Caregiver Activity',
            'device': 'Device Status',
            'alert': 'Alerts & Emergencies',
            'smart_connect': 'Smart Connect'
        };
        return labels[category] || category;
    };

    const showAllowSwitch = !hasActivePushSubscription && canEditEnablePush;
    const showPushWarning = !hasActivePushSubscription && hasPushTemplatesEnabled;
    const hasPushPaperContent = showAllowSwitch || showPushWarning;

    const handleSaveToastClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSaveToastOpen(false);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Notification Preferences for {caregiverName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure how this caregiver receives notifications. Changes are applied immediately upon saving.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {useApp === 1 && !(showDeviceScopedStatus && hasActivePushSubscription) && hasPushPaperContent && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                        {showAllowSwitch ? (
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                Allow Push Notifications
                            </Typography>
                        ) : null}
                        {showAllowSwitch && (
                            <Tooltip title={enablePushTooltip} arrow placement="top">
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={hasActivePushSubscription}
                                                onChange={(e) => handlePushSubscriptionToggle(e.target.checked)}
                                                size="small"
                                                disabled={ pushStatusUpdating}
                                            />
                                        }
                                        label={hasActivePushSubscription ? 'Allowed' : ''}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                    {showPushWarning && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            {showDeviceScopedStatus && pushPermission === 'denied'
                                ? 'Push notifications are disabled for Connect App 365 in this device\'s system settings. Enable notifications there, then return to the app and try again.'
                                : 'Some Push Notifications are enabled below, but this caregiver has not allowed Push Notifications on any devices.'}
                        </Alert>
                    )}
                </Paper>
            )}

            <Box sx={{ mb: 2 }}>
                {Object.entries(groupedTemplates).map(([category, templates]) => (
                    <Accordion key={category} defaultExpanded={category === 'alerts'}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                {getCategoryLabel(category)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {templates.map((template) => (
                                <Box key={template.TemplateName} sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                        {template.Description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
                                        {/* Email and Push - only for app users */}
                                        {useApp === 1 && (
                                            <>
                                                <Tooltip title={deliveryMethodTooltips.email} arrow placement="top">
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={getPreferenceValue(template.TemplateName, category, 'EmailEnabled')}
                                                                onChange={() => handleToggle(template.TemplateName, category, 'EmailEnabled')}
                                                                size="small"
                                                            />
                                                        }
                                                        label="Email"
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    title={getPushPreferenceTooltip(getPreferenceValue(template.TemplateName, category, 'PushEnabled'))}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <FormControlLabel
                                                        sx={
                                                            getPreferenceValue(template.TemplateName, category, 'PushEnabled') && !hasActivePushSubscription
                                                                ? (theme) => ({
                                                                    px: 0.75,
                                                                    borderRadius: 1,
                                                                    border: '1px solid',
                                                                    borderColor: theme.palette.warning.main,
                                                                    backgroundColor: theme.palette.mode === 'dark'
                                                                        ? 'rgba(245, 158, 11, 0.12)'
                                                                        : 'rgba(245, 158, 11, 0.08)',
                                                                    '& .MuiTypography-root': {
                                                                        color: 'text.primary',
                                                                        fontWeight: 500
                                                                    }
                                                                })
                                                                : undefined
                                                        }
                                                        control={
                                                            <Switch
                                                                checked={getPreferenceValue(template.TemplateName, category, 'PushEnabled')}
                                                                onChange={() => handleToggle(template.TemplateName, category, 'PushEnabled')}
                                                                size="small"
                                                            />
                                                        }
                                                        label="Push"
                                                    />
                                                </Tooltip>
                                            </>
                                        )}
                                        {/* SMS option - for non-app users in alert category, or for app users with smart_connect in alert category */}
                                        {((useApp === 0 && category === 'alert') || (useApp === 1 && template.TemplateName.includes('smart_connect') && smartConnectUnit && category === 'alert')) && (
                                            <Tooltip title={deliveryMethodTooltips.sms} arrow placement="top">
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={getPreferenceValue(template.TemplateName, category, 'SMSEnabled')}
                                                            onChange={() => handleToggle(template.TemplateName, category, 'SMSEnabled')}
                                                            size="small"
                                                        />
                                                    }
                                                    label="SMS"
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Divider sx={{ mt: 1 }} />
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                    {saving ? 'Saving...' : 'Save Preferences'}
                </Button>
            </Box>

            <Snackbar
                open={saveToastOpen}
                autoHideDuration={3000}
                onClose={handleSaveToastClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSaveToastClose} severity="success" sx={{ width: '100%' }}>
                    {saveToastMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default NotificationPreferencesEditor;
